import { apiGet, apiPost, apiPut, apiRoutes, buildApiRoute } from "./entityApiShared.js";
import { requireCmtsIdentifier } from "../core/payloads/cmtsTypedPayloads.js";
import { requireCmtsGuid, optionalCmtsGuid } from "../core/utils/cmtsGuid.js";
import {
  parseRcaRecord,
  parseRcaRecordList,
  type RcaActionRequest,
  type RcaCreateRequest,
  type RcaDto,
  type RcaUpdateRequest,
} from "../../types/rca.types.js";
import { parseEligibleLifecycleUsers } from "./lifecycleEligibleUsers.js";
import type { DropdownOption } from "../types/api.types.js";

export type { RcaActionRequest, RcaCreateRequest, RcaDto, RcaRecordDto, RcaUpdateRequest } from "../../types/rca.types.js";

type BackendRcaCreateRequest = Readonly<Omit<RcaCreateRequest, "complaintId">>;
type BackendRcaUpdateRequest = Readonly<Omit<RcaUpdateRequest, "rowVersion"> & { expectedRowVersion?: string | undefined }>;
type BackendRcaActionRequest = Readonly<{
  remarks?: string | undefined;
  reason?: string | undefined;
  expectedRowVersion?: string | undefined;
}>;
type BackendRcaCompleteRequest = Readonly<{
  investigationSummary: string;
  rootCause: string;
  completedStatusId: string;
  expectedRowVersion?: string | undefined;
}>;

function optionalText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

function createRequest(payload: RcaCreateRequest): BackendRcaCreateRequest {
  return {
    rcaMethodId: optionalCmtsGuid(payload.rcaMethodId) ?? EMPTY_GUID,
    rootCauseCategoryId: optionalCmtsGuid(payload.rootCauseCategoryId) ?? EMPTY_GUID,
    problemStatement: payload.problemStatement.trim(),
    ...(optionalText(payload.investigationSummary) ? { investigationSummary: payload.investigationSummary!.trim() } : {}),
    ...(optionalText(payload.rootCause) ? { rootCause: payload.rootCause!.trim() } : {}),
    ownerUserId: optionalCmtsGuid(payload.ownerUserId) ?? EMPTY_GUID,
    targetDate: payload.targetDate || new Date(Date.now() + 7 * 86400000).toISOString(),
    statusId: optionalCmtsGuid(payload.statusId) ?? EMPTY_GUID,
  };
}

function updateRequest(payload: RcaUpdateRequest): BackendRcaUpdateRequest {
  const { rowVersion, ...business } = payload;
  return {
    ...business,
    ...(payload.rcaMethodId ? { rcaMethodId: requireCmtsGuid(payload.rcaMethodId, "RCA update rcaMethodId") } : {}),
    ...(payload.rootCauseCategoryId ? { rootCauseCategoryId: requireCmtsGuid(payload.rootCauseCategoryId, "RCA update rootCauseCategoryId") } : {}),
    ...(payload.ownerUserId ? { ownerUserId: requireCmtsGuid(payload.ownerUserId, "RCA update ownerUserId") } : {}),
    ...(rowVersion ? { expectedRowVersion: rowVersion } : {}),
  };
}

function actionRequest(payload: RcaActionRequest): BackendRcaActionRequest {
  return {
    ...(optionalText(payload.remarks) ? { remarks: payload.remarks!.trim() } : {}),
    ...(optionalText(payload.reason) ? { reason: payload.reason!.trim() } : {}),
    ...(payload.rowVersion ? { expectedRowVersion: payload.rowVersion } : {}),
  };
}

function completeRequest(payload: RcaActionRequest): BackendRcaCompleteRequest {
  const investigationSummary = optionalText(payload.investigationSummary);
  const rootCause = optionalText(payload.rootCause);
  if (!investigationSummary) throw new Error("investigationSummary is required to complete RCA.");
  if (!rootCause) throw new Error("rootCause is required to complete RCA.");
  return {
    investigationSummary,
    rootCause,
    completedStatusId: requireCmtsGuid(payload.completedStatusId, "RCA complete completedStatusId"),
    ...(payload.rowVersion ? { expectedRowVersion: payload.rowVersion } : {}),
  };
}

export const rcaApi = {
  permittedOwners: async (complaintId: string, signal?: AbortSignal): Promise<readonly DropdownOption[]> =>
    parseEligibleLifecycleUsers(await apiGet<unknown>(
      buildApiRoute(apiRoutes.rca.permittedOwners, {
        complaintId: requireCmtsIdentifier(complaintId, "complaintId"),
      }),
      { signal },
    )),
  queue: async (signal?: AbortSignal): Promise<readonly RcaDto[]> => parseRcaRecordList(await apiGet<unknown>(buildApiRoute(apiRoutes.rca.queue), { signal })),
  list: async (complaintId: string, signal?: AbortSignal): Promise<readonly RcaDto[]> => {
    const id = requireCmtsIdentifier(complaintId, "complaintId");
    return parseRcaRecordList(await apiGet<unknown>(buildApiRoute(apiRoutes.rca.list, { complaintId: id }), { signal }));
  },
  get: async (complaintId: string, id: string, signal?: AbortSignal): Promise<RcaDto> => parseRcaRecord(await apiGet<unknown>(buildApiRoute(apiRoutes.rca.get, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "rcaId") }), { signal })),
  create: async (complaintId: string, payload: RcaCreateRequest, idempotencyKey?: string): Promise<RcaDto> => parseRcaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.rca.create, { complaintId: requireCmtsIdentifier(complaintId, "complaintId") }), createRequest(payload), { idempotencyKey })),
  update: async (complaintId: string, id: string, payload: RcaUpdateRequest, idempotencyKey?: string): Promise<RcaDto> => parseRcaRecord(await apiPut<unknown>(buildApiRoute(apiRoutes.rca.update, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "rcaId") }), updateRequest(payload), { idempotencyKey })),
  submit: async (complaintId: string, id: string, payload: RcaActionRequest, idempotencyKey?: string): Promise<RcaDto> => parseRcaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.rca.submit, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "rcaId") }), actionRequest(payload), { idempotencyKey })),
  approve: async (complaintId: string, id: string, payload: RcaActionRequest, idempotencyKey?: string): Promise<RcaDto> => parseRcaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.rca.approve, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "rcaId") }), actionRequest(payload), { idempotencyKey })),
  reject: async (complaintId: string, id: string, payload: RcaActionRequest, idempotencyKey?: string): Promise<RcaDto> => parseRcaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.rca.reject, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "rcaId") }), actionRequest(payload), { idempotencyKey })),
  complete: async (complaintId: string, id: string, payload: RcaActionRequest, idempotencyKey?: string): Promise<RcaDto> => parseRcaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.rca.complete, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "rcaId") }), completeRequest(payload), { idempotencyKey })),
  reopen: async (complaintId: string, id: string, payload: RcaActionRequest, idempotencyKey?: string): Promise<RcaDto> => parseRcaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.rca.reopen, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "rcaId") }), actionRequest(payload), { idempotencyKey })),
};
